import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "../store";
import { useAuthToken } from "../AuthProvider";
import { toast } from "react-toastify";
import { LinearProgress, Box } from "@mui/material";
import { Tooltip } from "react-tooltip";
import info_mark from "../img/info_mark.svg";
import { User, UserScore } from "../Profile";
import "react-tooltip/dist/react-tooltip.css";

export interface QuestionnaireParams {
  callback: () => void;
}

export function Questionnaire(params: QuestionnaireParams) {
  const dispatch = useDispatch();
  const userData = useSelector((state: AppState) => state.user); // get the current user object from the Redux store
  const [response, setResponse] = React.useState<string>("");
  const [bullet, setBullet] = React.useState<string>("");
  const [question, setQuestion] = React.useState<string>("");
  const [qid, setQid] = React.useState<string>("");
  const [numQuestions, setNumQuestions] = React.useState<number>(0);
  const [answeredQuestions, setAnsweredQuestions] = React.useState<number>(0);
  const [typedText, setTypedText] = React.useState("");
  const [showCursor, setShowCursor] = React.useState(false);
  const [isLoadingOverlayOpen, setIsLoadingOverlayOpen] = React.useState(false);
  const [disableButtons, setDisableButtons] = React.useState<boolean>(false);
  const { isAuthenticated, token, isLoading, updateToken } = useAuthToken();
  const navigate = useNavigate();

  useEffect(() => {
    if (question !== undefined) {
      let index = 0;
      const intervalId = setInterval(() => {
        if (index <= question.length) {
          setTypedText(question.slice(0, index));
          index++;
        } else {
          setShowCursor(false);
          clearInterval(intervalId);
        }
      }, 20);

      setShowCursor(true);

      return () => clearInterval(intervalId);
    }
  }, [question, bullet]);

  useEffect(() => {
    if (!isLoading) {
      if (userData.resume_uuid == "") {
        navigate("/");
      }
    }
  }, [userData.resume_uuid, isLoading]);

  const updateUserResume = (uuid: string) => {
    const updatedUser: User = {
      ...userData, // create a new object with all existing properties of the user object
      resume_uuid: uuid,
    };
    dispatch({
      type: "UPDATE_STATE",
      payload: updatedUser,
    });
  };

  async function handleResponse(response: Response) {
    if (response.ok) {
      const responseData = await response.json();

      if (responseData.question !== undefined) {
        setQuestion(responseData.question);
        setBullet(responseData.bullet);
        setQid(responseData.qid);
        setAnsweredQuestions(responseData.answered_questions);
        setNumQuestions(responseData.num_questions);
      } else if (responseData.message !== undefined) {
        const uuid = responseData.uuid as string;
        updateUserResume(uuid);
        params.callback();
      }
    }
  }

  async function getQuestion() {
    if (isLoading) {
      return;
    }

    const apiEndpoint =
      
      "/api/questionnaire?file=" +
      userData.resume_uuid;

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    try {
      // Make a POST request using fetch
      const response = await fetch(apiEndpoint, {
        method: "GET",
        headers: headers,
      });

      handleResponse(response);
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occured.");
    }
  }

  async function sendAnswer(skip = false, regen = false) {
    if (response.trim().length == 0 && !skip && !regen) {
      return;
    }

    const apiEndpoint =  "/api/questionnaire";

    const formData = new FormData();
    formData.append("qid", qid);

    if (skip) {
      formData.append("skip", "skip");
    } else if (regen) {
      formData.append("regen", "regen");
    } else {
      formData.append("answer", response);
    }

    formData.append("file", userData.resume_uuid);
    formData.append("preferred_template", userData.preferred_template);

    const headers = {
      Authorization: `Bearer ${token}`,
    };

    setDisableButtons(true);

    try {
      // Make a POST request using fetch
      const response = await fetch(apiEndpoint, {
        method: "POST",
        body: formData,
        headers: headers,
      });

      handleResponse(response);
    } catch (error) {
      console.error("Error:", error);
    }

    setDisableButtons(false);

    setResponse("");
  }

  useEffect(() => {
    if (!isLoading) {
      getQuestion();
    }
  }, [isLoading]);

  const submit = () => {
    sendAnswer();
  };

  return (
    <div>
      {isLoadingOverlayOpen && (
        <div className="overlay">
          <div className="agreement-content">
            <h2 className="attention-text font-bold">Loading...</h2>
            <h2 className="overlay-text font-bold">
              Please be patient, this could take about a minute...
            </h2>
            <div className="text-center">
              <div role="status">
                <svg
                  aria-hidden="true"
                  className="inline h-8 w-8 animate-spin fill-blue-600 text-gray-200 dark:text-gray-600"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex max-w-full flex-row">
        <div className="flex w-full flex-col">
          <div className="px-2 lg:px-8 2xl:px-16 2xl:py-12">
            <div className="p-8">
              <div className="mb-6">
                <div className="mb-4 flex flex-row lg:mb-8">
                  <Tooltip
                    anchorSelect=".question-tooltip"
                    className="font-code"
                    place="bottom"
                  >
                    Number of questions determined by length of resume and score
                  </Tooltip>
                  <p className="font-code text-base font-bold lg:text-lg 2xl:text-xl">
                    Question {answeredQuestions + 1} of {numQuestions}
                  </p>
                  <img src={info_mark} className={`question-tooltip ml-3 `} />
                </div>
                <p className="mb-4 font-code text-sm font-bold lg:mb-8 2xl:text-lg">
                  The following question regards this bullet point:
                </p>
                <p className="font-code text-sm font-bold lg:text-lg 2xl:text-xl">
                  {bullet}
                </p>
              </div>

              <div className="2xl:mb-18 mb-6 lg:mb-10">
                <p className="font-code font-bold md:text-lg xl:text-xl 2xl:text-2xl">
                  <span>
                    {typedText} {showCursor && "|"}
                  </span>
                </p>
              </div>

              <textarea
                className="xl:h-30 focus:shadow-outline h-20 w-full rounded border p-2 focus:outline-none 2xl:h-40"
                placeholder="Type your response here..."
                value={response}
                onChange={(e) => setResponse(e.target.value)}
              />

              <div className="mt-4 flex flex-col space-y-4">
                <button
                  className="focus:shadow-outline rounded bg-[#00b8e4] px-4 py-4 font-bold text-white hover:bg-[#029ac0] focus:outline-none 2xl:py-6"
                  onClick={submit}
                  disabled={disableButtons}
                >
                  Submit
                </button>
                <div className="flex flex-row justify-between">
                  <Tooltip
                    anchorSelect=".diff-question-tooltip"
                    className="font-code"
                    place="top"
                  >
                    Ask something else about the same bullet point
                  </Tooltip>
                  <button
                    className="diff-question-tooltip focus:shadow-outline mr-2 w-1/2 rounded bg-[#ff8800] px-4 py-4 font-bold text-white hover:bg-[#da7400] focus:outline-none lg:mr-4 2xl:py-6"
                    onClick={() => {
                      sendAnswer(false, true);
                    }}
                    disabled={disableButtons}
                  >
                    Different Question
                  </button>
                  <Tooltip
                    anchorSelect=".skip-question-tooltip"
                    className="font-code"
                    place="top"
                  >
                    Skip question entirely, do not replace with new one
                  </Tooltip>
                  <button
                    className="skip-question-tooltip focus:shadow-outline ml-2 w-1/2 rounded bg-[#ff8800] px-4 py-4 font-bold text-white hover:bg-[#da7400] focus:outline-none lg:ml-4 2xl:py-6"
                    onClick={() => {
                      sendAnswer(true, true);
                    }}
                    disabled={disableButtons}
                  >
                    Skip
                  </button>
                </div>
              </div>
            </div>
          </div>
          <Box sx={{ width: "100%", color: "#ff8800" }}>
            <LinearProgress
              variant="determinate"
              value={(answeredQuestions * 100) / numQuestions}
              color="inherit"
              style={{
                height: 20,
                backgroundColor: "#F5F5F5",
              }}
            />
          </Box>
        </div>
      </div>
    </div>
  );
}
